/**
 * Survey composition: assembles modular scales + topic pages into
 * the exact { preSurvey, postSurvey, consent, debriefing } shape
 * that SurveyEngine and the backend expect.
 *
 * Pure function — no side effects, no API calls.
 */

// Auto-discover all scale modules
const scaleContext = require.context("./survey_scales", false, /^\.\/scale_.*\.json$/);
const ALL_SCALES = {};
scaleContext.keys().forEach((filename) => {
  const scale = scaleContext(filename);
  ALL_SCALES[scale.scaleId] = scale;
});

// Auto-discover all topic modules
const topicContext = require.context("./survey_topics", false, /^\.\/topic_.*\.json$/);
const ALL_TOPICS = {};
topicContext.keys().forEach((filename) => {
  const topic = topicContext(filename);
  ALL_TOPICS[topic.topicId] = topic;
});

/**
 * Scale categories for the admin UI toggle groups.
 * Each category groups related scales for display.
 */
const SCALE_CATEGORIES = [
  {
    id: "pre_background",
    label: "Background & Media",
    phase: "pre",
    scaleIds: ["demographics", "media_politics"],
  },
  {
    id: "pre_dispositions",
    label: "Dispositions & Trust",
    phase: "pre",
    scaleIds: ["need_for_cognition", "need_for_closure", "dispositions_views", "social_trust_pre"],
  },
  {
    id: "pre_involvement",
    label: "Topic Involvement",
    phase: "pre",
    scaleIds: ["topic_involvement"],
  },
  {
    id: "post_affective",
    label: "Affective & Perceptual",
    phase: "post",
    scaleIds: [
      "emotional_response",
      "discussion_quality",
      "argument_quality",
      "manipulation_checks",
    ],
  },
  {
    id: "post_trust",
    label: "Trust, Validity & Platform",
    phase: "post",
    scaleIds: ["trust_efficacy", "final_reflections"],
  },
];

/**
 * Deep-strip admin-only metadata fields (e.g. "source") from page objects
 * so they don't leak into participant-facing survey data stored in the DB.
 */
function stripAdminMetadata(obj) {
  if (Array.isArray(obj)) return obj.map(stripAdminMetadata);
  if (obj && typeof obj === "object") {
    const result = {};
    for (const [k, v] of Object.entries(obj)) {
      if (k === "source") continue;
      result[k] = stripAdminMetadata(v);
    }
    return result;
  }
  return obj;
}

/**
 * Compose a full survey from enabled scales + a topic.
 *
 * @param {Object} options
 * @param {string[]} options.enabledScaleIds - IDs of shared scales to include
 * @param {Object} options.topic - A topic module object (from ALL_TOPICS)
 * @param {string[]} [options.excludedQuestionIds] - Question IDs to exclude ("scaleId.questionId")
 * @param {Object} [options.allScales] - Scale map (defaults to ALL_SCALES)
 * @returns {{ preSurvey: Object, postSurvey: Object, consent: Object, debriefing: Object }}
 */
function composeSurvey({ enabledScaleIds, topic, excludedQuestionIds = [], allScales = ALL_SCALES }) {
  const excludedSet = new Set(excludedQuestionIds);

  // Filter out excluded questions from a page, returning null if no questions remain.
  // Info-only pages (questions: []) are kept regardless — they exist for their
  // title/instruction (e.g. the topic_intro_transition cover-story bridge).
  function filterPage(page, keyPrefix) {
    if (!excludedSet.size) return page;
    const original = page.questions || [];
    if (original.length === 0) return page;
    const filtered = original.filter(
      (q) => !excludedSet.has(`${keyPrefix}.${q.questionId}`)
    );
    if (filtered.length === 0) return null;
    return { ...page, questions: filtered };
  }

  // Collect all pages with their phase + order
  const entries = [];

  // Add enabled shared scales
  for (const scaleId of enabledScaleIds) {
    const scale = allScales[scaleId];
    if (!scale) continue;
    const page = filterPage(scale.page, scaleId);
    if (!page) continue;
    entries.push({
      phase: scale.phase,
      order: scale.order,
      page,
    });
  }

  // Add topic-specific pages
  if (topic) {
    for (const [pageKey, entry] of Object.entries(topic.pages)) {
      const page = filterPage(entry.page, `topic.${pageKey}`);
      if (!page) continue;
      entries.push({
        phase: entry.phase,
        order: entry.order,
        page,
      });
    }
  }

  // Split by phase, sort by order, strip admin-only metadata from output
  const prePages = entries
    .filter((e) => e.phase === "pre")
    .sort((a, b) => a.order - b.order)
    .map((e) => stripAdminMetadata(e.page));

  const postPages = entries
    .filter((e) => e.phase === "post")
    .sort((a, b) => a.order - b.order)
    .map((e) => stripAdminMetadata(e.page));

  return {
    preSurvey: { pages: prePages },
    postSurvey: { pages: postPages },
    consent: topic?.consent || { title: "Study Information & Consent", content: "", requireAcknowledgment: true },
    debriefing: topic?.debriefing || { title: "Study Debriefing", content: "" },
  };
}

/**
 * Survey presets: predefined scale/question selections.
 * "recommended" = moderate cut (drops secondary DVs, trims moderators).
 * "full" = everything enabled.
 */
const SURVEY_PRESETS = {
  streamlined: {
    label: "Streamlined",
    description: "Core DVs, essential moderators (incl. need for closure), manipulation checks + stimulus perception (~67 items incl. reading pages, ~24 min)",
    disabledScaleIds: [
      "argument_quality",    // Redundant with manipulation checks
      "trust_efficacy",      // Secondary DV — generalized trust change
      "social_trust_pre",    // Only needed as baseline for trust_efficacy
      "need_for_cognition",  // Secondary moderator — complexity is already between-subjects
      "dispositions_views",  // Need for Chaos + Punitive Authoritarianism — floor-effected in a general sample; better suited to a targeted study
    ],
    excludedQuestionIds: [
      // Topic involvement: keep importance, moral conviction 1, familiarity
      // Drop relevance (overlaps importance), care (overlaps importance), moral conviction 2 (overlaps mc1)
      "topic_involvement.involvement_relevance",
      "topic_involvement.involvement_care",
      "topic_involvement.moral_conviction_2",
      // Validity: keep AI realism items, drop behavioral intentions
      "final_reflections.behavioral_intentions",
      // Interpersonal: keep thermometers, drop social distance (awkward for policy topics)
      "topic.interpersonal.social_distance",
    ],
  },
  full: {
    label: "Full",
    description: "All scales and questions (~131 items, ~40 min)",
    disabledScaleIds: [],
    excludedQuestionIds: [],
  },
};

/**
 * Get all scale IDs (convenience for "all enabled" default).
 */
function getAllScaleIds() {
  return Object.keys(ALL_SCALES);
}

/**
 * Get scale IDs for a given preset (filters out disabled scales).
 */
function getPresetScaleIds(presetId = "streamlined") {
  const preset = SURVEY_PRESETS[presetId] || SURVEY_PRESETS.streamlined;
  const disabled = new Set(preset.disabledScaleIds);
  return Object.keys(ALL_SCALES).filter((id) => !disabled.has(id));
}

/**
 * Get excluded question IDs for a given preset.
 */
function getPresetExcludedQuestionIds(presetId = "streamlined") {
  const preset = SURVEY_PRESETS[presetId] || SURVEY_PRESETS.streamlined;
  return [...preset.excludedQuestionIds];
}

/**
 * Count total questions across a set of pages.
 */
function countQuestions(pages) {
  return pages.reduce((sum, p) => sum + (p.questions?.length || 0), 0);
}

/**
 * Count total visible items across questions.
 * For likert_battery: counts each item. For all other types: counts as 1.
 */
function countItems(questions) {
  if (!questions) return 0;
  return questions.reduce((sum, q) => {
    if (q.type === "likert_battery" && q.items) return sum + q.items.length;
    if (q.type === "stimulus_perception" && q.perceptionItems) return sum + q.perceptionItems.length;
    return sum + 1;
  }, 0);
}

/**
 * Count total visible items across a set of pages.
 */
function countPageItems(pages) {
  return pages.reduce((sum, p) => sum + countItems(p.questions), 0);
}

module.exports = { composeSurvey, ALL_SCALES, ALL_TOPICS, SCALE_CATEGORIES, SURVEY_PRESETS, getAllScaleIds, getPresetScaleIds, getPresetExcludedQuestionIds, countQuestions, countItems, countPageItems };
